import { useEffect, useState } from "react";
import { Undo2, X } from "lucide-react";

interface Props {
  /** Short label describing the action that just happened
   *  ("Panneau « Jojo Mardi » fermé."). */
  message: string;
  /** Auto-dismiss + executes the trailing action. Default 5s. */
  durationMs?: number;
  /** Click handler for the Annuler button. The toast self-dismisses
   *  once this is called — caller doesn't need to manage state. */
  onUndo: () => void;
  /** Fired when the toast self-dismisses (timeout or X). The caller
   *  uses this to clear its `recentlyClosed` state. */
  onDismiss: () => void;
}

/**
 * 5-second undo toast — the safety net for irreversible-feeling
 * actions (panneau close).
 *
 * Pattern: Gmail-style. Bottom-center, low-key, with a single
 * Annuler CTA and a small × to dismiss early. The bar progress
 * is the timer made visible — operators see exactly how long the
 * undo window is.
 */
export function UndoToast({
  message,
  durationMs = 5000,
  onUndo,
  onDismiss,
}: Props) {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Tick the progress bar 50× per second of duration so the
    // animation reads as smooth without flooding React renders.
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, 100 - (elapsed / durationMs) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        window.clearInterval(interval);
        onDismiss();
      }
    }, 50);
    return () => window.clearInterval(interval);
  }, [durationMs, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30 max-w-md"
    >
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-full bg-ink text-white shadow-lg ring-1 ring-white/10">
        <span className="text-sm">{message}</span>
        <button
          type="button"
          onClick={() => {
            onUndo();
            onDismiss();
          }}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-white/10 hover:bg-white/20 transition"
        >
          <Undo2 className="h-3 w-3" />
          Annuler
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Fermer"
          className="text-white/60 hover:text-white"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {/* Progress bar — visualizes the undo window so operators
          aren't surprised by the cliff. */}
      <div className="mx-4 mt-1 h-0.5 rounded-full bg-ink/10 dark:bg-white/10 overflow-hidden">
        <div
          className="h-full bg-chip-orange transition-[width] duration-50"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
